<template>
  <div v-if="isReadView" class="read-view d-flex flex-row align-items-center">
    <i class="el-input__icon el-icon-date"></i>
    <p class="m-0">{{ dateValue || $i18n.t('not_set') }}</p>
  </div>
  <div v-else class="input-group date-picker">
    <calendar-select
      v-if="isCalendarView"
      :name="name"
      :type="type"
      :date-value="dateValue"
      :read-only="computedReadOnly"
      :min-date="minExp"
      :max-date="maxExp"
      :calendar-properties="calendarProperties"
      @date-selected="onDateSelected"
      :size="size"
    ></calendar-select>
    <el-time-select
      ref="timeSelect"
      v-else-if="noCalendar && isFieldTimeSelect"
      v-model="timeValue"
      :class="{ invalid: isInvalid }"
      :name="name"
      :placeholder="placeholder"
      :editable="checkEditable"
      :picker-options="timePickerOptions"
      :size="size"
    >
    </el-time-select>
    <el-time-picker
      ref="dateTimePicker"
      v-else-if="noCalendar"
      v-model="timeValue"
      :class="{ invalid: isInvalid }"
      :name="name"
      :placeholder="placeholder"
      :editable="checkEditable"
      :format="getTimeFormat"
      value-format="HH:mm:ss"
      :size="size"
    >
    </el-time-picker>
    <el-date-picker
      ref="dateTimeSelect"
      v-else
      v-model="dateValue"
      :class="{ invalid: isInvalid }"
      :disabled="computedReadOnly"
      :name="name"
      :type="getComponentType"
      :placeholder="placeholder"
      :picker-options="pickerOptions"
      :editable="checkEditable"
      :format="getDateFormat"
      :value-format="getDateFormat"
      :size="size"
    >
    </el-date-picker>
  </div>
</template>

<script>
import { isEmpty, isNull, isUndefined, isDate, get, isString } from 'lodash'
import moment from 'moment'
import { FIELD_TYPES } from '../../../consts'
import CalendarSelect from './CalendarSelect'

const US_TIME_FORMAT = 'hh:mm:ss A'
const DEFAULT_TIME_FORMAT = 'HH:mm:ss'

const US_DATE_FORMAT = 'MM/dd/yyyy'
const HU_DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'

export default {
  name: 'date-time',
  components: { CalendarSelect },
  props: {
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    type: {
      type: String,
    },
    value: {
      type: [String, Number, Date],
      default: null,
    },
    directInputEntry: {
      type: Boolean,
      default: false,
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    formats: {
      type: Object,
    },
    minExp: {
      type: [String, Number],
      default: '',
    },
    maxExp: {
      type: [String, Number],
      default: '',
    },
    isReadView: {
      type: Boolean,
      default: false,
    },
    isCalendarView: {
      type: Boolean,
      default: false,
    },
    calendarProperties: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: String,
      default: 'medium',
    },
  },
  data() {
    return {
      pickerOptions: {
        firstDayOfWeek: 1,
        disabledDate: this.disabledDate,
      },
      enableTime:
        this.type === FIELD_TYPES.DATETIME_OLD || this.type === FIELD_TYPES.DATETIME || this.type === FIELD_TYPES.TIME,
      noCalendar: this.type === FIELD_TYPES.TIME,
    }
  },
  methods: {
    disabledDate(date) {
      let minDate = this.minExp || get(this.formats, 'min', '')
      let maxDate = this.maxExp || get(this.formats, 'max', '')
      minDate = new Date(minDate)
      maxDate = new Date(maxDate)
      if (this.type === FIELD_TYPES.DATE) {
        minDate.setDate(minDate.getDate() - 1)
        minDate.setHours(23, 59, 59)
        maxDate.setHours(23, 59, 59)
      }
      return date < minDate || date >= maxDate
    },
    onDateSelected(date) {
      this.dateValue = date
    },
  },
  computed: {
    needTime() {
      return this.type === FIELD_TYPES.DATETIME_OLD || this.type === FIELD_TYPES.DATETIME
    },
    timeValue: {
      get() {
        return this.getTimeValue
      },
      set(selectedTime) {
        this.$emit('form-item-changed', {
          name: this.name,
          value: selectedTime,
        })
        this.$emit('input', { name: this.name, value: selectedTime })
      },
    },
    dateValue: {
      get() {
        return this.getValue
      },
      set(selectedDate) {
        if (this.noCalendar && selectedDate !== null) {
          const hours = selectedDate.split(':')[0]
          const minutes = selectedDate.split(':')[1]
          selectedDate = new Date(null, null, null, hours, minutes)
        }
        this.$emit('form-item-changed', {
          name: this.name,
          value: selectedDate,
        })
        this.$emit('input', { name: this.name, value: selectedDate })
      },
    },
    valueIdentifiesAsDate() {
      return (
        (!isNull(this.value) && !isUndefined(this.value) && !isEmpty(this.value)) ||
        isDate(this.value) ||
        typeof this.value === 'number'
      )
    },
    getTimeValue() {
      return this.value ? this.value : null
    },
    getValue() {
      if (this.valueIdentifiesAsDate) {
        const format = this.noCalendar ? this.getTimeFormat : this.getDateFormat
        const momentFormat = format.replace('dd', 'DD')
        if ((this.type === this.type) === FIELD_TYPES.DATE && isString(this.value)) {
          const thisValue = this.value.replace('Z', '')
          return moment(thisValue).format(momentFormat)
        }
        return this.value ? moment(new Date(this.value)).format(momentFormat) : ''
      }
      return null
    },
    getTimeFormat() {
      const timeFormat = get(this, 'formats.timeFormat')
      const languageCode = get(this, '$CORE.language.code')

      if (timeFormat) {
        return timeFormat
      }
      if (languageCode === 'en-US') {
        return US_TIME_FORMAT
      }
      return DEFAULT_TIME_FORMAT
    },
    getDateFormat() {
      let format = ''
      switch (this.$CORE.language.code) {
        case 'en-US': {
          format = US_DATE_FORMAT
          break
        }
        case 'hu-HU':
        case 'de-DE': {
          format = HU_DATE_FORMAT
          break
        }
        default: {
          format = DEFAULT_DATE_FORMAT
          break
        }
      }
      if (this.needTime) {
        format += ` ${this.getTimeFormat}`
      }
      return format
    },
    getComponentType() {
      return this.needTime ? 'datetime' : 'date'
    },
    computedReadOnly() {
      return this.readOnly || this.groupReadOnly
    },
    checkEditable() {
      return this.directInputEntry && !this.computedReadOnly
    },
    isFieldTimeSelect() {
      return get(this.formats, 'isTimeSelect', false)
    },
    timePickerOptions() {
      const { startTime, timeStep, endTime } = this.formats
      return {
        start: startTime,
        step: timeStep,
        end: endTime,
      }
    },
  },
}
</script>

<style lang="scss">
.read-view {
  background-color: $default-panel-header-bg;
  padding: 0.2em 0.5em !important;
  border-radius: 0.25em !important;
  margin-top: 0.5em;
  line-height: normal;
  gap: 0.25em;
  i,
  p {
    color: $color-text-regular;
  }
}

.read-view.input-group.date-picker:last-of-type:first-of-type .el-input__inner {
  border: none !important;
}
</style>
